import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AnimalsQuiz.css";

import parrotImg from "../assets/animals/parrot.png";
import blueMagpieImg from "../assets/animals/bluemagpie.png";
import butterflyImg from "../assets/animals/butterfly.png";
import monkeyImg from "../assets/animals/monkey.png";
import squirrelImg from "../assets/animals/squirrel.png";
import peacockImg from "../assets/animals/peacock.png";
import elephantImg from "../assets/animals/elephant.png";
import tigerImg from "../assets/animals/tiger.png";
import lizardImg from "../assets/animals/lizard.png";

import animalBadge from "../assets/badges/animalbadge.png";
import quizResultImg from "../assets/General/quizresult.png";

// how many questions get asked per round
const TOTAL_QUESTIONS = 5;

// api base falls back to localhost if the env variable isn't set
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// full list of animals with their image, display title and fun fact
const ANIMALS = [
  {
    key: "parrot",
    title: "Sri Lanka Hanging Parrot",
    img: parrotImg,
    fact: "This animal hangs like a bat when it sleeps at night",
  },
  {
    key: "blue-magpie",
    title: "Sri Lanka Blue Magpie",
    img: blueMagpieImg,
    fact: "These animals are only found in Sri Lanka's rainforests",
  },
  {
    key: "butterfly",
    title: "Yellow Orange Tip Butterfly",
    img: butterflyImg,
    fact: "This animal is able camouflage with dead leaves and flowers to help it hide from predators.",
  },
  {
    key: "monkey",
    title: "Toque Macaque Monkey",
    img: monkeyImg,
    fact: "This animal has a swirl on its head like a hat, which is where its name comes from",
  },
  {
    key: "squirrel",
    title: "Sri Lankan Palm Squirrel",
    img: squirrelImg,
    fact: "This little animal is omnivorous, it eats plants and small insects",
  },
  {
    key: "lizard",
    title: "Sri Lankan Lizard",
    img: lizardImg,
    fact: "This animal can change colour depending on temperature or mood ",
  },
  {
    key: "elephant",
    title: "Sri Lankan Elephant",
    img: elephantImg,
    fact: "These animals gather together, and the gatherings are one of the largest in the world",
  },
  {
    key: "tiger",
    title: "Sri Lankan Leapord",
    img: tigerImg,
    fact: "Yala National Park in Sri Lanka has one of the largest populations of this animal",
  },
  {
    key: "peacock",
    title: "Indian Peacock",
    img: peacockImg,
    fact: "These animals can shake their giant feathers to create a beautiful dance",
  },
];

// fisher-yates shuffle, returns a new array and leaves the original untouched
function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// picks TOTAL_QUESTIONS random animals as answers and builds 3-option questions for each
function buildQuestions() {
  const chosenAnswers = shuffle(ANIMALS).slice(0, TOTAL_QUESTIONS);

  return chosenAnswers.map((correct) => {
    // grab 2 wrong options from everything except the correct animal
    const wrongOptions = shuffle(
      ANIMALS.filter((item) => item.key !== correct.key)
    ).slice(0, 2);

    // shuffle the three options so the correct one isn't always first
    const options = shuffle([correct, ...wrongOptions]);

    return {
      id: correct.key,
      fact: correct.fact,
      correctKey: correct.key,
      correctTitle: correct.title,
      options,
      selectedKey: null,
    };
  });
}

// tries a few common localStorage keys to find the logged-in player's name
function getUserName() {
  const candidates = [
    "exploreLankaUser",
    "exploreLankaUsername",
    "currentUser",
    "user",
    "username",
    "name",
  ];

  for (const key of candidates) {
    const stored = localStorage.getItem(key);
    if (!stored) continue;

    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed === "string" && parsed.trim()) return parsed.trim();
      if (parsed?.name) return String(parsed.name).trim();
      if (parsed?.user?.name) return String(parsed.user.name).trim();
      if (parsed?.username) return String(parsed.username).trim();
    } catch {
      // if it wasn't valid json just use the raw string
      if (stored.trim()) return stored.trim();
    }
  }

  return "";
}

// reusable home button so it doesn't need to be copy-pasted into every screen
function HomeButton({ navigate }) {
  return (
    <button
      className="quizHomeBtn"
      onClick={() => navigate("/loading", { state: { to: "/home" } })}
      aria-label="Go Home"
      title="Go Home"
      type="button"
    >
      {/* inline svg so the fill colour is easy to override in css */}
      <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="quizHomeBtn__icon"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
          fill="#ffffff"
        />
      </svg>
    </button>
  );
}

export default function AnimalsQuiz() {
  const navigate = useNavigate();

  // plays a short cheer sound when the quiz finishes
  function playYaySound() {
    try {
      const yay = new Audio("/audio/yay.mp3");
      yay.volume = 1;
      yay.play().catch(() => {});
    } catch (error) {
      console.warn("Yay sound failed:", error);
    }
  }

  // plays a fanfare when a badge is awarded
  function playBadgeSound() {
    try {
      const badge = new Audio("/audio/badge.mp3");
      badge.volume = 1;
      badge.play().catch(() => {});
    } catch (error) {
      console.warn("Badge sound failed:", error);
    }
  }

  // false until the player clicks "start quiz" on the intro screen
  const [hasStarted, setHasStarted] = useState(false);

  // the array of question objects for this round
  const [questions, setQuestions] = useState(() => buildQuestions());

  // index of whichever question is currently on screen
  const [currentIndex, setCurrentIndex] = useState(0);

  // the key of the option the player just clicked
  const [selectedKey, setSelectedKey] = useState(null);

  // flips to true after an answer is chosen so the feedback banner shows
  const [showFeedback, setShowFeedback] = useState(false);

  // running correct-answer count shown in the top bar
  const [score, setScore] = useState(0);

  // controls whether the badge popup is visible
  const [showBadgePopup, setShowBadgePopup] = useState(false);

  // true while the post-quiz api call is in flight
  const [savingResult, setSavingResult] = useState(false);

  // best score fetched from the api on load, null means not loaded yet
  const [bestScore, setBestScore] = useState(null);

  // ref so the save effect can check it without needing it as a dependency
  const hasSavedResultRef = useRef(false);

  // refs that track score and answers synchronously so the save effect gets accurate data
  const scoreRef = useRef(0);
  const answersRef = useRef([]);

  const currentQuestion = questions[currentIndex];

  // once currentIndex goes past the last question the quiz is done
  const isFinished = currentIndex >= questions.length;

  // "1 / 5" style label for the top bar
  const progressText = useMemo(() => {
    return `${Math.min(currentIndex + 1, questions.length)} / ${questions.length}`;
  }, [currentIndex, questions.length]);

  // fetch the player's personal best before the intro screen is shown
  useEffect(() => {
    if (hasStarted) return;

    async function fetchBestScore() {
      const playerName = getUserName();
      if (!playerName) return;

      try {
        const url = `${API_BASE}/api/best-score/${encodeURIComponent(playerName)}/animals_quiz`;
        const res = await fetch(url);
        const data = await res.json();

        if (data?.best_score !== null && data?.best_score !== undefined) {
          setBestScore(data.best_score);
        }
      } catch (err) {
        console.warn("[AnimalsQuiz] fetchBestScore error:", err);
      }
    }

    fetchBestScore();
  }, [hasStarted]);

  // resets all state and starts a fresh round
  function startQuiz() {
    setQuestions(buildQuestions());
    setCurrentIndex(0);
    setSelectedKey(null);
    setShowFeedback(false);
    setScore(0);
    setShowBadgePopup(false);
    hasSavedResultRef.current = false;
    scoreRef.current = 0;
    answersRef.current = [];
    setHasStarted(true);
  }

  // called when the player taps an answer card
  function handleAnswer(optionKey) {
    // ignore extra clicks while feedback is already showing
    if (showFeedback) return;

    const isCorrect = optionKey === currentQuestion.correctKey;

    // update the ref straight away so the save effect sees the right value
    if (isCorrect) scoreRef.current += 1;

    // record this answer for the api payload
    answersRef.current = [
      ...answersRef.current,
      {
        question_fact: currentQuestion.fact,
        correct_answer: currentQuestion.correctTitle,
        correct_key: currentQuestion.correctKey,
        selected_key: optionKey,
        selected_answer:
          currentQuestion.options.find((option) => option.key === optionKey)?.title || "",
        was_correct: isCorrect,
      },
    ];

    setSelectedKey(optionKey);
    setShowFeedback(true);

    // also stamp the selected key onto the question object for the results screen
    setQuestions((prev) =>
      prev.map((question, index) =>
        index === currentIndex ? { ...question, selectedKey: optionKey } : question
      )
    );

    if (isCorrect) {
      setScore((prev) => prev + 1);
    }
  }

  // advances to the next question and clears the feedback state
  function handleNext() {
    setCurrentIndex((prev) => prev + 1);

    if (currentIndex < questions.length - 1) {
      setSelectedKey(null);
      setShowFeedback(false);
    }
  }

  // goes back to the intro screen and wipes all round state
  function handlePlayAgain() {
    setHasStarted(false);
    setQuestions(buildQuestions());
    setCurrentIndex(0);
    setSelectedKey(null);
    setShowFeedback(false);
    setScore(0);
    setShowBadgePopup(false);
    hasSavedResultRef.current = false;
    scoreRef.current = 0;
    answersRef.current = [];
  }

  // fires once when the last question is answered and sends the result to the api
  useEffect(() => {
    async function saveQuizResult() {
      if (!isFinished || hasSavedResultRef.current) return;

      hasSavedResultRef.current = true;
      setSavingResult(true);
      playYaySound();

      const finalScore = scoreRef.current;
      const finalAnswers = answersRef.current;
      const playerName = getUserName();

      if (!playerName) {
        console.warn("[AnimalsQuiz] no player name found, score not saved.");
        setSavingResult(false);
        return;
      }

      try {
        const payload = {
          name: playerName,
          page_name: "animals_quiz",
          score: finalScore,
          total_questions: questions.length,
          badge_key: "animals_master",
          answers: finalAnswers,
        };

        const response = await fetch(`${API_BASE}/api/quiz-result`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!response.ok) {
          console.error("[AnimalsQuiz] save failed:", data);
          return;
        }

        // update the displayed best score if the api returns a new one
        if (data?.best_score !== null && data?.best_score !== undefined) {
          setBestScore(data.best_score);
        }

        // if this run unlocked a badge, show the popup after a short delay
        if (data?.newly_awarded_badge === true) {
          playBadgeSound();
          setTimeout(() => setShowBadgePopup(true), 500);
        }

      } catch (error) {
        console.error("[AnimalsQuiz] network error:", error);
      } finally {
        setSavingResult(false);
      }
    }

    saveQuizResult();
  }, [isFinished, questions.length]);

  // intro screen shown before the player starts
  if (!hasStarted) {
    return (
      <div className="animalsQuizPage">
        <header className="animalsQuizHeader">
          <div className="animalsQuizHeaderTop">
            <HomeButton navigate={navigate} />
          </div>

          <h1 className="animalsQuizTitle">Animals Quiz Adventure!</h1>
          <p className="animalsQuizSubtitle">
            Match each Sri Lankan animal to the fact you learned.
          </p>
        </header>

        <main className="animalsQuizWrap">
          <section className="animalsIntroPanel">
            {/* only render the best score line if the api has returned a value */}
            {bestScore !== null && (
              <p className="animalsIntroBestScore">
                Your best score: <strong>{bestScore} / {TOTAL_QUESTIONS}</strong>
              </p>
            )}

            <h2 className="animalsIntroTitle">How to play</h2>

            <div className="animalsIntroSteps">
              <div className="animalsIntroStep">
                <span className="animalsIntroStepNumber">1</span>
                <p>Read the animal fact carefully</p>
              </div>
              <div className="animalsIntroStep">
                <span className="animalsIntroStepNumber">2</span>
                <p>Look at the 3 animal picture choices</p>
              </div>
              <div className="animalsIntroStep">
                <span className="animalsIntroStepNumber">3</span>
                <p>Click the animal that matches the fact</p>
              </div>
              <div className="animalsIntroStep">
                <span className="animalsIntroStepNumber">4</span>
                <p>Get 5 out of 5 to win the special animals badge!</p>
              </div>
            </div>

            <div className="animalsIntroActions">
              <button className="animalsStartQuizBtn" onClick={startQuiz} type="button">
                Start Quiz
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  // results screen shown after the last question is answered
  if (isFinished) {
    return (
      <div className="animalsQuizPage">
        <header className="animalsQuizHeader">
          <div className="animalsQuizHeaderTop">
            <HomeButton navigate={navigate} />
          </div>

          <h1 className="animalsQuizTitle">Animals Quiz Adventure!</h1>
        </header>

        <main className="animalsQuizWrap">
          <section className="animalsResultPanel">
            <img
              src={quizResultImg}
              alt="Quiz result"
              className="animalsQuizResultImage"
            />

            <h2 className="animalsQuizResultTitle">You finished the quiz!</h2>

            {/* count correct answers directly from the question objects */}
            <p className="animalsQuizResultScore">
              Your score:{" "}
              <span>{questions.filter((q) => q.selectedKey === q.correctKey).length}</span>
              {" "}/ {questions.length}
            </p>

            {savingResult && <p className="animalsQuizSavingText">Saving your score...</p>}

            <div className="animalsQuizResultActions">
              <button className="animalsQuizActionBtn" onClick={handlePlayAgain} type="button">
                Play Again
              </button>
              <button
                className="animalsQuizActionBtn secondary"
                onClick={() => navigate("/animals")}
                type="button"
              >
                Back to Animals Game
              </button>
            </div>
          </section>
        </main>

        {/* badge popup appears on top of everything if the player earned it */}
        {showBadgePopup && (
          <div className="animalsBadgeOverlay" onClick={() => setShowBadgePopup(false)}>
            <div className="animalsBadgePopup" onClick={(e) => e.stopPropagation()}>
              <h2 className="animalsBadgePopupTitle">Badge Unlocked!</h2>
              <p className="animalsBadgePopupText">
                You got 5 out of 5 and earned the Animals Badge!
              </p>
              <img src={animalBadge} alt="Animals badge" className="animalsBadgeImage" />
              <button
                className="animalsQuizActionBtn"
                onClick={() => setShowBadgePopup(false)}
                type="button"
              >
                Yay!
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // main question screen
  return (
    <div className="animalsQuizPage">
      <header className="animalsQuizHeader">
        <div className="animalsQuizHeaderTop">
          <HomeButton navigate={navigate} />
        </div>

        <h1 className="animalsQuizTitle">Animals Quiz Adventure!</h1>
        <p className="animalsQuizSubtitle">
          Which animal matches this fact?
        </p>
      </header>

      {/* progress and score pills */}
      <div className="animalsQuizTopBar">
        <span className="animalsQuizPill">Question: <b>{progressText}</b></span>
        <span className="animalsQuizPill">Score: <b>{score}</b></span>
      </div>

      <main className="animalsQuizWrap">
        <section className="animalsQuestionPanel">
          <p className="animalsQuizPromptLabel">Which animal matches this fact?</p>

          {/* the fact panel shows the clue the player needs to match */}
          <div className="animalsFactPanel">
            <p className="animalsFactText">{currentQuestion.fact}</p>
          </div>

          <div className="animalsQuizOptionsGrid">
            {currentQuestion.options.map((option) => {
              const isSelected = selectedKey === option.key;
              const isCorrect = showFeedback && option.key === currentQuestion.correctKey;
              const isWrong =
                showFeedback &&
                isSelected &&
                option.key !== currentQuestion.correctKey;

              return (
                <button
                  key={option.key}
                  className={`animalsQuizOptionCard ${isSelected ? "selected" : ""} ${
                    isCorrect ? "correct" : ""
                  } ${isWrong ? "wrong" : ""}`}
                  onClick={() => handleAnswer(option.key)}
                  type="button"
                  disabled={showFeedback}
                >
                  <div className="animalsQuizOptionImageWrap">
                    <img
                      src={option.img}
                      alt={option.title}
                      className="animalsQuizOptionImage"
                    />
                  </div>
                  <span className="animalsQuizOptionLabel">{option.title}</span>
                </button>
              );
            })}
          </div>

          {/* feedback banner shows after the player picks an answer */}
          {showFeedback && (
            <div
              className={`animalsQuizFeedback ${
                selectedKey === currentQuestion.correctKey ? "good" : "bad"
              }`}
            >
              {selectedKey === currentQuestion.correctKey
                ? "Correct! Great job!"
                : `Oops! The correct answer is ${currentQuestion.correctTitle}.`}
            </div>
          )}

          {/* next button only appears once an answer has been submitted */}
          {showFeedback && (
            <div className="animalsQuizNextRow">
              <button className="animalsQuizActionBtn" onClick={handleNext} type="button">
                {currentIndex === questions.length - 1 ? "See Results" : "Next Question"}
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}